// @flow weak
import * as Cookie from 'app/packages/cookie-utils';
import CONST from 'app/common/lib/Const';
import navigateAfterLoginStatusChange from 'app/common/lib/util/navigateAfterLoginStatusChange';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import storage from 'app/common/lib/Storage';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import urlUtil from 'app/common/lib/urlUtil';

export const MODAL_TYPES = {
  SOFT_BANNED: 0,
  RATE_LIMITED: 1,
  LOGIN_ASSISTANCE: 2,
  PASSWORD_RESET_SENT: 3,
  PREVENT_FACEBOOK_REGISTER: 4,
  PREVENT_GOOGLE_REGISTER: 5,
  SUSPENDED: 6,
};

export type ModalTypes = $Values<typeof MODAL_TYPES>;

const ACCOUNT_RECOVERY_ERRORS = [
  CONST.API_LIMIT_EXCEEDED_ERROR, // 8
  CONST.API_EVENT_BLOCKED_ERROR, // 9
  CONST.USER_IN_SAFE_MODE, // 19
  CONST.POLICY_VIOLATION_USER, // 83
  CONST.LOGIN_BAD_PASSWORD_ASK_RESET, // 85
  CONST.LOGIN_HARD_BANNED_USER, // 88
  CONST.RESPONSE_CODE_TOO_MANY_REQUESTS, // 429
];

export const RECOGNIZED_LOGIN_ERRORS = [
  ...ACCOUNT_RECOVERY_ERRORS,
  CONST.INVALID_PASSWORD_FB_USER,
  CONST.INVALID_PASSWORD_GOOGLE_USER,
  CONST.LOGIN_VOLUNTARILY_DEACTIVATED_USER,
  CONST.INVALID_PASSWORD_ERROR_CODE,
  CONST.POLICY_VIOLATION_USER,
];

const filteredBadPasswordError = ACCOUNT_RECOVERY_ERRORS.filter(
  (err) => err !== CONST.LOGIN_BAD_PASSWORD_ASK_RESET && err !== CONST.POLICY_VIOLATION_USER,
);

const ERRORS_TO_MODIFY = [...filteredBadPasswordError, CONST.INVALID_PASSWORD_ERROR_CODE];

class loginUtil {
  static defaultLoginRedirectUrl() {
    return window.location.pathname !== CONST.GEN.templateConst.settings.PASSWORD_RESET_URL &&
      window.location.pathname !== CONST.GEN.templateConst.settings.LOGIN_URL
      ? window.location.pathname + window.location.search
      : '/';
  }

  static isExceedRecaptchaLocalLimit(): boolean {
    const recaptchaTimeStr = storage.localStorage.getItem(
      PCONST.LOCALSTORAGE_UNAUTH_RECAPTCHA_HISTORY,
    );
    if (recaptchaTimeStr && recaptchaTimeStr.indexOf('|') > -1) {
      //No more than 3 submits within an hour
      const recaptchaHistory = recaptchaTimeStr.split('|');
      return (
        recaptchaHistory.length === 3 &&
        new Date().getTime() / 1000 - parseInt(recaptchaHistory[0], 10) <= 3600
      );
    }
    return false;
  }

  static updateRecaptchaTimeStamp() {
    // Maintain last 3 timestamps
    const recaptchaTimeStr = storage.localStorage.getItem(
      PCONST.LOCALSTORAGE_UNAUTH_RECAPTCHA_HISTORY,
    );
    let newRecaptchaHistoryStr = '',
      oldRecaptchaHistory = [];
    if (recaptchaTimeStr) {
      oldRecaptchaHistory = recaptchaTimeStr.split('|');
      if (oldRecaptchaHistory.length === 3) {
        // Pop out the oldest time record
        oldRecaptchaHistory.shift();
      }
    }
    const timestamp = (new Date().getTime() / 1000).toString();

    oldRecaptchaHistory.push(timestamp);
    newRecaptchaHistoryStr = oldRecaptchaHistory.join('|');
    storage.localStorage.setItem(
      PCONST.LOCALSTORAGE_UNAUTH_RECAPTCHA_HISTORY,
      newRecaptchaHistoryStr,
    );
  }

  static isFullyLoggedIn(isAuthenticated: boolean, loginState: number): boolean {
    return isAuthenticated && loginState === PCONST.LoginStates.DEFAULT;
  }

  static handleRedirect(nextUrl: ?string) {
    if (!nextUrl) {
      nextUrl = '/';
      const urlParams = urlUtil.getQueryStringParams();
      if (urlParams) {
        const nextRedirectParam = urlParams.next;
        if (nextRedirectParam) {
          nextUrl = nextRedirectParam;
          delete urlParams.next;
        }
        nextUrl = urlUtil.appendQueryString(nextUrl, urlParams);
      }
    }
    navigateAfterLoginStatusChange(nextUrl);
  }

  static handleRedirectWithFallbackWindowLocation() {
    this.handleRedirect(this.defaultLoginRedirectUrl());
  }

  /**
   * Redirect a new user to the either the nextUrl param passed in or NUX
   * @param nextUrl the nextUrl query param that indicates where a user should be taken after sign up
   * @param disableNextUrl disables the functionality of the nextUrl and takes the user to NUX
   */
  static handleSignupRedirect(nextUrl, disableNextUrl) {
    storage.localStorage.setItem(PCONST.LOCALSTORAGE_SIGNUP_TIME, Date.now());

    if (!disableNextUrl && nextUrl && nextUrl !== '/') {
      loginUtil.handleRedirect(nextUrl);
    } else {
      loginUtil.handleRedirect('/');
    }
  }

  static storeLoginCredentialsToBrowser(creds: Object) {
    declare var navigator: {
      credentials?: { store(cred: Object): Promise<*> },
    };
    if (navigator.credentials && creds.username_or_email) {
      try {
        const passwordCred = new window.PasswordCredential({
          id: creds.username_or_email,
          password: creds.password,
        });
        navigator.credentials.store(passwordCred).then(() => {
          trackRegisterAction('navigatorCredentials.store.success');
        });
      } catch (err) {
        trackRegisterAction('navigatorCredentials.store.error');
      }
    }
  }

  static retrieveLoginCredentialsFromBrowser() {
    const browserCredentials: ?Object = window.navigator.credentials;
    if (!browserCredentials) {
      return new Promise((resolve, reject) => {
        reject();
      });
    }

    return browserCredentials.get({
      password: true,
      mediation: 'optional',
    });
  }

  // The 'creds' argument are NOT normalized for the resource layer. They should be credentials that were retrieved
  // via retrieveLoginCredentialsFromBrowser or via, window.navigator.credentials.get
  static attemptLoginWithBrowserCredentials(creds, csrfToken, appState) {
    if (!creds) {
      // No credentials returned
      return new Promise((resolve, reject) => {
        reject('No credentials');
      });
    }

    let form;
    let normalizedResourceCredentials;
    if (!creds.password) {
      // If `password` is `undefined`, this API is older version
      // which means this is Chrome 51 - 59
      normalizedResourceCredentials = creds;
    } else {
      // If `password` is not `undefined`, this API is new version
      // which means this is Chrome 60 or later
      normalizedResourceCredentials = 'include';
      form = new FormData();
      form.append('username', creds.id);
      form.append('password', creds.password);
    }

    /* (javery)
     * 1. Using an alternate resource to create a session because the POST formData will be in a particular format
     *     that cannot be supported with the UserSessionResource and routing logic in /routes/common.py.
     * 2. The native browser's experimental fetch method must be used as Chrome depends on it, to properly inject
     *     the user's browser credentials for automatic login.
     */
    const resourceOptions = {
      method: 'POST',
      body: form,
      credentials: normalizedResourceCredentials,
      headers: {
        Accept: 'application/json, text/javascript, */*',
        'X-CSRFToken': csrfToken,
        'X-Pinterest-AppState': appState,
        'X-Requested-With': 'XMLHttpRequest',
      },
    };

    return fetch('/resource/UserSessionResourceFetch/create/', resourceOptions)
      .then((response) => {
        if (response.status !== 200) {
          const error: Object = new Error(response.statusText);
          error.response = response;
          throw error;
        }
        trackRegisterAction('navigatorCredentials.login.success');
        return response.json();
      })
      .catch((error) => {
        trackRegisterAction('navigatorCredentials.login.failure');
        return Promise.reject(error);
      });
  }

  static fetchRecentlyLoggedOutUser(context) {
    if (context && context.isBot) {
      return Promise.reject();
    }

    const unauthUserDataResource = ResourceFactory.create('UnauthUserDataResource');
    return new Promise((resolve, reject) => {
      unauthUserDataResource
        .callGet()
        .then((resourceResponse) => {
          const userInfo = resourceResponse.resource_response.data;
          const logged_out_cookie = !!Cookie.get('logged_out');
          if (!!userInfo) {
            if (
              !userInfo.connected_to_facebook &&
              !userInfo.connected_to_google &&
              !userInfo.has_password
            ) {
              trackRegisterAction('unauth.personalized_login.no_login_methods_found');
            }

            trackRegisterAction(
              `unauth.personalized_login.fetch_user_info.info.user_info_found.logged_out_cookie_${
                logged_out_cookie ? 'true' : 'false'
              }`,
            );

            return resolve(userInfo);
          }
          trackRegisterAction(
            `unauth.personalized_login.fetch_user_info.info.no_user_info.logged_out_cookie_${
              logged_out_cookie ? 'true' : 'false'
            }`,
          );
          return reject({});
        })
        .catch((error) => {
          if (error.message) {
            const errorType = error.message.replace(/\s/g, '_').toLowerCase();
            trackRegisterAction(`unauth.personalized_login.fetch_user_info.error.${errorType}`);
          } else {
            trackRegisterAction('unauth.personalized_login.fetch_user_info.error.unknown');
          }
          return reject(error);
        });
    });
  }

  static handleLoginMfa(creds, data, onLoginWithMfa) {
    trackRegisterAction('multi_step_login');
    loginUtil.storeLoginCredentialsToBrowser(creds);
    // redirect to the LoginMfa screen
    if (onLoginWithMfa) {
      onLoginWithMfa(creds, data);
    } else {
      trackRegisterAction('unauth.login.error.CLIENT_ERROR.null_mfa_handler');
    }
  }

  static getLoginErrorState(
    error,
    source,
    email,
    touchableErrorMessage,
    updateAccountRecoveryModal,
    seoUnauthExperiments,
  ) {
    let errorState = null;
    const api_error_code =
      error.http_status === CONST.RESPONSE_CODE_TOO_MANY_REQUESTS
        ? CONST.RESPONSE_CODE_TOO_MANY_REQUESTS
        : error.api_error_code;

    let message;
    if (ERRORS_TO_MODIFY.includes(api_error_code)) {
      message = touchableErrorMessage({
        api_error_code,
        errorMessage: error.message,
        handleTouch: () => updateAccountRecoveryModal(MODAL_TYPES.PASSWORD_RESET_SENT),
        source,
        userSearch: email,
        seoUnauthExperiments,
      });
    } else {
      message = error.message;
    }

    if (message) {
      switch (api_error_code) {
        case CONST.INVALID_PASSWORD_ERROR_CODE:
        case CONST.LOGIN_BAD_PASSWORD_ASK_RESET:
          errorState = { passwordValidationError: message };
          break;

        case CONST.INVALID_PASSWORD_FB_USER:
          if (
            seoUnauthExperiments &&
            (
              seoUnauthExperiments.v2GetGroup('web_unauth_social_only_set_password') || ''
            ).startsWith('enabled_text')
          ) {
            const touchableMessage = touchableErrorMessage({
              api_error_code,
              errorMessage: error.message,
              handleTouch: () => updateAccountRecoveryModal(MODAL_TYPES.PASSWORD_RESET_SENT),
              source,
              userSearch: email,
              seoUnauthExperiments,
            });
            errorState = { passwordValidationError: touchableMessage };
          } else {
            errorState = { facebookValidationError: message };
          }
          break;

        case CONST.INVALID_PASSWORD_GOOGLE_USER:
          if (
            seoUnauthExperiments &&
            (
              seoUnauthExperiments.v2GetGroup('web_unauth_social_only_set_password') || ''
            ).startsWith('enabled_text')
          ) {
            const touchableMessage = touchableErrorMessage({
              api_error_code,
              errorMessage: error.message,
              handleTouch: () => updateAccountRecoveryModal(MODAL_TYPES.PASSWORD_RESET_SENT),
              source,
              userSearch: email,
              seoUnauthExperiments,
            });
            errorState = { passwordValidationError: touchableMessage };
          } else {
            errorState = { googleValidationError: message };
          }
          break;

        case CONST.INVALID_EMAIL_ERROR_CODE:
          errorState = { emailValidationError: message };
          break;

        default:
          errorState = { emailValidationError: message };
          break;
      }
    } else {
      trackRegisterAction(`unauth.login.error.SERVER_ERROR.unknown.${api_error_code}.${source}`);
    }

    return errorState;
  }
}

export default loginUtil;
